/**
 * Auto-syncs gateway configs to cloud whenever they change.
 *
 * Watches the gateway store for changes and uploads to cloud
 * when the user is authenticated. Also listens for settings-changed
 * CustomEvents (e.g. subagent-depth-change) so cloud-synced settings
 * written outside the gateway store are uploaded promptly.
 *
 * Debounces to avoid rapid-fire API calls during setup flows.
 * Skips the initial render — the initial sync is handled by AppWithAuth.
 */

import { useEffect, useRef, useState } from 'react';
import { useGatewayStore } from '../gateway/store';
import { useAuth } from '../auth/AuthContext';
import { getApiUrl } from '../utils/environment';
import { collectCloudSettings } from '../settings';

/** Debounce delay before uploading (ms) */
const SYNC_DEBOUNCE_MS = 2000;

/** CustomEvents that should trigger a cloud settings upload */
const SETTINGS_CHANGE_EVENTS = [
  'subagent-depth-change',
  'settings-changed',
] as const;

/**
 * Upload current localStorage settings to cloud.
 * Duplicates the logic from App.tsx's uploadLocalSettingsToCloud
 * but is self-contained so the hook doesn't depend on that function.
 */
async function syncToCloud(getAuthHeaders: () => Record<string, string>): Promise<void> {
  try {
    const baseUrl = getApiUrl();
    const headers = getAuthHeaders();
    if (!headers.Authorization) return;

    const settings = collectCloudSettings();

    const hasData = Object.values(settings).some((v) => v !== null);
    if (!hasData) return;

    await fetch(`${baseUrl}/api/user/settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify({ settings }),
    });
  } catch {
    // Silently ignore — non-critical
  }
}

export function useCloudGatewaySync(): void {
  const { state } = useGatewayStore();
  const { isAuthenticated, getAuthHeaders } = useAuth();

  const isInitialRef = useRef(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Bump counter when settings-change events fire, so the sync effect re-runs
  const [settingsBump, setSettingsBump] = useState(0);

  useEffect(() => {
    const handler = () => setSettingsBump((c) => c + 1);
    for (const evt of SETTINGS_CHANGE_EVENTS) {
      window.addEventListener(evt, handler);
    }
    return () => {
      for (const evt of SETTINGS_CHANGE_EVENTS) {
        window.removeEventListener(evt, handler);
      }
    };
  }, []);

  useEffect(() => {
    // Skip initial render — AppWithAuth handles the first sync
    if (isInitialRef.current) {
      isInitialRef.current = false;
      return;
    }

    if (!isAuthenticated) return;

    // Debounce: clear previous timer, set a new one
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      void syncToCloud(getAuthHeaders);
    }, SYNC_DEBOUNCE_MS);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [state.gateways, settingsBump, isAuthenticated, getAuthHeaders]);
}
